import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface QuoteItemRow {
  quoteItemId: number;
  sortOrder: number;
  description: string;
  quantity: number;
  unitPriceExGst: number;
  lineTotalExGst: number;
  notes?: string | null;
}

interface Props {
  items: QuoteItemRow[];
  onChange: (next: QuoteItemRow[]) => void;
  readonly?: boolean;
}

export function QuoteItemsGrid({ items, onChange, readonly }: Props) {
  const total = useMemo(() => items.reduce((s, i) => s + i.lineTotalExGst, 0), [items]);

  const patch = (id: number, field: keyof QuoteItemRow, value: string | number) => {
    onChange(
      items.map((i) => {
        if (i.quoteItemId !== id) return i;
        const next: QuoteItemRow = {
          ...i,
          [field]: field === "description" || field === "notes" ? value : Number(value),
        };
        next.lineTotalExGst = Math.round(next.quantity * next.unitPriceExGst * 100) / 100;
        return next;
      }),
    );
  };

  return (
    <div>
      <table className="w-full text-sm" aria-label="Quote items">
        <thead>
          <tr>
            <th className="text-left">Description</th>
            <th className="text-right w-24">Quantity</th>
            <th className="text-right w-32">Unit price ex-GST</th>
            <th className="text-right w-32">Line total ex-GST</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.quoteItemId}>
              <td>
                <Input
                  value={i.description}
                  disabled={readonly}
                  onChange={(e) => patch(i.quoteItemId, "description", e.target.value)}
                  aria-label="Description"
                />
              </td>
              <td>
                <Input
                  type="number"
                  value={i.quantity}
                  disabled={readonly}
                  onChange={(e) => patch(i.quoteItemId, "quantity", e.target.value)}
                  aria-label="Quantity"
                />
              </td>
              <td>
                <Input
                  type="number"
                  value={i.unitPriceExGst}
                  disabled={readonly}
                  onChange={(e) => patch(i.quoteItemId, "unitPriceExGst", e.target.value)}
                  aria-label="Unit price"
                />
              </td>
              <td className="text-right">${i.lineTotalExGst.toFixed(2)}</td>
              <td>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={readonly}
                  onClick={() => onChange(items.filter((x) => x.quoteItemId !== i.quoteItemId))}
                >
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 text-right">
        <strong>Total ex-GST: ${total.toFixed(2)}</strong>
      </div>
    </div>
  );
}
